import * as React from 'react';
import { XIIcon } from '@inmoment/the-kitchen';
import { TitleText } from '../../cookbook-ui/custom-components/TitleText/TitleText';
import { RowItem, Table } from '../../cookbook-ui/custom-components/Table/Table';
import { CLASS_PROP_COLUMNS, COMPLEX_COMPONENTS, INTERFACE_PROP_COLUMNS } from '../../utils/copy';
import { getComplexComponentWarningString, getLinkTo, getStandardTypeValues } from '../../utils/fns';
import { Playground } from '../automated-pages/playgroundsPlayground';
import { switchFurtherDescription } from '../custom-documentation/utils/switchFn';
import {
  KitchenClass,
  KitchenEnum,
  KitchenInterface,
  KitchenType,
  isComponent,
  NamedExport,
  KitchenVariable,
  KitchenClassProperty,
  KitchenInterfaceProperty,
  KitchenFunction,
  KitchenFunctionParameter,
} from '../../utils/Documentation';
import * as examples from '../../examples/index';
import styled from 'styled-components';
import { CodeBlock } from '../../cookbook-ui/custom-components/CodeBlock/CodeBlock';
import { Example } from '../../examples/helpers/model';
import { ExampleView } from '../../examples/helpers/Example';

const TemplateWrapper = styled.div`
  box-sizing: border-box;
  height: 100%;
  width: 100%;
`;

interface InterfaceProps<T extends NamedExport = NamedExport> {
  data: T;
}
export const ExportTemplate: React.FC<InterfaceProps> = (props) => {
  const { data } = props;

  if (isComponent(data)) {
    return <ComponentTemplate data={data} />;
  }
  switch (data.kind) {
    case 'class':
      return <ClassTemplate data={data} />;
    case 'interface':
      return <InterfaceTemplate data={data} />;
    case 'enum':
      return <EnumTemplate data={data} />;
    case 'type':
      return <TypeTemplate data={data} />;
    case 'variable':
      return <VariableTemplate data={data} />;
    case 'function':
      return <FunctionTemplate data={data} />;
    default:
      return <CodeBlock code={JSON.stringify(data, undefined, '  ')} />;
  }
};

interface ExportWrapperProps<T extends NamedExport = NamedExport> extends InterfaceProps<T> {
  children?: React.ReactNode;
  /** Displayed before the description in the export's title */
  additionalInfo?: React.ReactNode | null;
}
const ExportWrapper: React.FC<ExportWrapperProps> = (props) => {
  const { children, data, additionalInfo } = props;
  const { name, description: descriptionInput = '{No description}', kind } = data;
  const description = descriptionInput.replace(/^Description: /, '');
  const headerText = (
    <>
      {getLinkTo(name, { style: { color: 'unset' } })} ({isComponent(data) ? 'component' : kind})
    </>
  );
  const footerText = (
    <>
      {additionalInfo ? (
        <>
          {additionalInfo}
          <br />
          <br />
        </>
      ) : null}
      <pre style={{ margin: 0, whiteSpace: 'break-spaces' }}>Description: {description}</pre>
    </>
  );
  return (
    <TemplateWrapper id={name}>
      <br />
      <TitleText headerText={headerText} footerText={footerText} />
      <hr />
      {children}
      <br />
    </TemplateWrapper>
  );
};

export const TypeTemplate = (props: InterfaceProps<NamedExport & KitchenType>) => {
  return (
    <ExportWrapper {...props}>
      <CodeBlock code={props.data.type} />
    </ExportWrapper>
  );
};

export const EnumTemplate = (props: InterfaceProps<NamedExport & KitchenEnum>) => {
  return (
    <ExportWrapper {...props}>
      <CodeBlock code={props.data.properties.join('\n')} />
    </ExportWrapper>
  );
};

export const VariableTemplate = (props: InterfaceProps<NamedExport & KitchenVariable>) => {
  return (
    <ExportWrapper {...props}>
      <CodeBlock code={`export const ${props.data.name}: ${props.data.type};`} />
    </ExportWrapper>
  );
};

export const FunctionTemplate = (props: InterfaceProps<NamedExport & KitchenFunction>) => {
  const { data } = props;
  const rows = React.useMemo(
    () =>
      data.parameters
        ? formatParametersForTable({
            ...data,
            parameters: data.parameters.map((p) => ({ ...p, required: !p.optional })),
          })
        : [],
    [data],
  );
  return (
    <ExportWrapper {...props} additionalInfo={data.type ? <span>Returns: {getLinkTo(data.type)}</span> : undefined}>
      <div>
        <span className="kxi-text-600">Parameters:</span>
      </div>
      <br />
      {rows.length > 0 ? (
        <Table columns={CLASS_PROP_COLUMNS} rows={rows} customColumnWidth={classColumnWidths} uniformWidth={500} isPropTable />
      ) : (
        <div>This function takes no parameters.</div>
      )}
    </ExportWrapper>
  );
};

const classColumnWidths = [
  { index: 0, minWidth: 192, maxWidth: 'unset' },
  { index: 1, minWidth: 120, maxWidth: 'unset' },
  { index: 2, minWidth: 100, maxWidth: 'unset' },
  { index: 3, minWidth: 80, maxWidth: 'unset' },
  { index: 4, minWidth: 100, maxWidth: 'unset' },
  { index: 5, minWidth: 60, maxWidth: '60px', center: true },
];
export const ClassTemplate = (props: InterfaceProps<NamedExport & KitchenClass>) => {
  const { data } = props;
  const rows = React.useMemo(() => formatPropertiesForTable(data), [data]);
  const extendsName = data.extends;
  const implementsNames = data.implements;

  const heritage = (
    <>
      {extendsName ? <>extends {getLinkTo(extendsName)} </> : null}
      {implementsNames ? (
        <>
          implements{' '}
          {implementsNames.map((name, i) => (
            <React.Fragment key={name}>
              {getLinkTo(name)}
              {i < implementsNames.length - 1 ? ',' : undefined}
            </React.Fragment>
          ))}
        </>
      ) : null}
    </>
  );
  return (
    <ExportWrapper {...props} additionalInfo={heritage}>
      {rows.length > 0 ? (
        <Table columns={CLASS_PROP_COLUMNS} rows={rows} customColumnWidth={classColumnWidths} uniformWidth={500} isPropTable />
      ) : (
        <div>This class has no own properties (only those that it inherits from its superclass).</div>
      )}
    </ExportWrapper>
  );
};

const interfaceColumnWidths = classColumnWidths.slice().splice(CLASS_PROP_COLUMNS.indexOf('Default'), 1);
export const InterfaceTemplate = (props: InterfaceProps<NamedExport & KitchenInterface>) => {
  const { data } = props;
  const rows = React.useMemo(() => formatPropertiesForTable(data), [data]);
  const extendsNames = data.extends;
  const heritage = extendsNames ? (
    <>
      extends{' '}
      {extendsNames.map((name, i) => (
        <React.Fragment key={name}>
          {getLinkTo(name)}
          {i < extendsNames.length - 1 ? ', ' : null}
        </React.Fragment>
      ))}
    </>
  ) : null;
  return (
    <ExportWrapper {...props} additionalInfo={heritage}>
      {rows.length > 0 ? (
        <Table columns={INTERFACE_PROP_COLUMNS} rows={rows} customColumnWidth={interfaceColumnWidths} uniformWidth={500} isPropTable />
      ) : null}
    </ExportWrapper>
  );
};

const getExamples = (name: string) => (examples as any)?.[`${name}_examples`] as Example[] | undefined;

export const ComponentTemplate = (props: InterfaceProps) => {
  const { data } = props;
  const { name } = data;
  const customDocs = switchFurtherDescription(name);
  const examples = getExamples(name);

  return (
    <ExportWrapper {...props}>
      <div>
        Accepts {getLinkTo(`${name}Props`)} and {getLinkTo(`${name}Events`)}
      </div>
      <br />
      <br />

      {customDocs ? (
        <>
          <br />
          <br />
          {customDocs}
          <br />
          <br />
        </>
      ) : null}

      {examples ? (
        <>
          <br />
          <br />
          <ExampleView examples={examples} componentName={name} />
          <br />
          <br />
        </>
      ) : null}

      {!COMPLEX_COMPONENTS.includes(name) ? (
        <Playground componentName={name} />
      ) : (
        <>
          <br />
          <br />
          {getComplexComponentWarningString(name)}
          <br />
          <br />
        </>
      )}
    </ExportWrapper>
  );
};

function formatPropertiesForTable(data: NamedExport & (KitchenClass | KitchenInterface)): RowItem[][] {
  let propRow: RowItem[][] = [];
  data.properties?.forEach((prop) => propRow.push(formatPropertyForTable(prop)));
  return propRow;
}
function formatParametersForTable(data: NamedExport & KitchenFunction): RowItem[][] {
  let propRow: RowItem[][] = [];
  data.parameters?.forEach((prop) => propRow.push(formatPropertyForTable(prop)));
  return propRow;
}

function formatPropertyForTable(data: KitchenFunctionParameter | KitchenClassProperty | KitchenInterfaceProperty): RowItem[] {
  const sanitizedType = (data.type?.includes('React.MouseEvent') ? 'MouseEvent' : data.type) ?? '{No type}';
  const typeValues = getStandardTypeValues(sanitizedType);

  const name = data.name ?? '{No name}';
  const description = data.description ?? '{No description}';
  const required: RowItem =
    'required' in data ? (
      data.required ? (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <XIIcon icon="circle_check" iconSize={30} />
        </div>
      ) : (
        ''
      )
    ) : (
      ''
    );

  if (data.kind === 'classProperty') {
    const defaultValue = data.default ? (['undefined', 'null'].includes(data.default) ? '' : data.default) : '';
    return [name, getLinkTo(sanitizedType), sanitizedType === typeValues ? '' : typeValues, defaultValue, description, required];
  }

  return [name, getLinkTo(sanitizedType), sanitizedType === typeValues ? '' : typeValues, description, required];
}
